import { motion } from 'framer-motion';
import { Shield, Eye, Clock, Lock, Check, Mail, Link2, AlertTriangle, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useUserCredits } from '@/hooks/useUserCredits';
import { toast } from 'sonner';
import type { CourageModeData, CourageMode, DeliveryMode, RevealPolicy, UserPlanTier } from '@/types/courage';
import { getPlanTier, canUseRevealWhenReady } from '@/types/courage';

interface CourageModeStepProps {
  courageData: CourageModeData;
  updateCourageData: (updates: Partial<CourageModeData>) => void;
  recipientName: string;
  saveAsDraft?: () => Promise<any>;
}

const courageOptions: {
  value: CourageMode;
  icon: typeof Eye;
  label: string;
  helper: string;
  requiresTier: UserPlanTier;
}[] = [
  {
    value: 'signed',
    icon: Eye,
    label: 'Signed Message',
    helper: 'Your name is shown to the recipient.',
    requiresTier: 'free',
  },
  {
    value: 'reveal_when_ready',
    icon: Clock,
    label: 'Reveal When Ready',
    helper: 'Start anonymous, then reveal your identity after a set time.',
    requiresTier: 'legacy',
  },
];

const deliveryOptions: { value: DeliveryMode; icon: typeof Link2; label: string; helper: string }[] = [
  { value: 'link', icon: Link2, label: 'Share a private link', helper: 'You share the link yourself' },
  { value: 'email_relay', icon: Mail, label: 'Send via email', helper: 'Say It NOW delivers it for you' },
];

const revealDayOptions = [1, 3, 7];

export function CourageModeStep({ courageData, updateCourageData, recipientName, saveAsDraft }: CourageModeStepProps) {
  const navigate = useNavigate();
  const { credits } = useUserCredits();
  const tier = getPlanTier(
    credits?.highest_bundle_purchased ?? null,
    credits?.has_purchased_bundle ?? false
  );

  const isLocked = (option: typeof courageOptions[number]): boolean => {
    if (option.requiresTier === 'free') return false;
    if (option.requiresTier === 'legacy') return !canUseRevealWhenReady(tier);
    return false;
  };

  const getLockedTooltip = (option: typeof courageOptions[number]): string => {
    if (option.value === 'reveal_when_ready') return 'Reveal When Ready is available in Legacy.';
    return '';
  };

  const handleCourageModeSelect = (mode: CourageMode) => {
    const updates: Partial<CourageModeData> = { courageMode: mode };
    if (mode === 'signed') {
      updates.revealPolicy = 'never';
      updates.revealAfterDays = null;
    }
    updateCourageData(updates);
  };

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-8">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
          <Shield className="h-8 w-8" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Courage Mode</h2>
        <p className="text-muted-foreground max-w-md mx-auto">
          Choose how you want to send this to {recipientName || 'your recipient'}. You're always in control.
        </p>
      </div>

      {/* Courage Mode Options */}
      <div className="max-w-md mx-auto space-y-3">
        <Label className="text-sm font-medium text-muted-foreground mb-2 block">How should this be sent?</Label>
        {courageOptions.map((option) => {
          const locked = isLocked(option);
          const isSelected = courageData.courageMode === option.value;
          const Icon = option.icon;

          const button = (
            <motion.button
              key={option.value}
              onClick={() => !locked && handleCourageModeSelect(option.value)}
              disabled={locked}
              className={cn(
                'w-full relative flex items-center gap-4 p-5 rounded-xl border-2 transition-all text-left',
                locked
                  ? 'border-border/50 opacity-60 cursor-not-allowed bg-muted/30'
                  : isSelected
                    ? 'border-primary bg-primary/5 shadow-md'
                    : 'border-border hover:border-primary/50 hover:bg-muted/50'
              )}
              whileHover={locked ? {} : { scale: 1.01 }}
              whileTap={locked ? {} : { scale: 0.99 }}
            >
              {isSelected && !locked && (
                <motion.div className="absolute top-3 right-3" initial={{ scale: 0 }} animate={{ scale: 1 }}>
                  <Check className="h-4 w-4 text-primary" />
                </motion.div>
              )}
              {locked && (
                <div className="absolute top-3 right-3">
                  <Lock className="h-4 w-4 text-muted-foreground" />
                </div>
              )}
              <div className={cn(
                'flex items-center justify-center h-10 w-10 rounded-full shrink-0',
                isSelected && !locked ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
              )}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex flex-col">
                <span className={cn('text-base font-medium', isSelected && !locked ? 'text-primary' : 'text-foreground')}>
                  {option.label}
                </span>
                <span className="text-xs text-muted-foreground mt-0.5">{option.helper}</span>
              </div>
            </motion.button>
          );

          if (locked) {
            return (
              <Tooltip key={option.value}>
                <TooltipTrigger asChild>{button}</TooltipTrigger>
                <TooltipContent><p>{getLockedTooltip(option)}</p></TooltipContent>
              </Tooltip>
            );
          }
          return button;
        })}

        {/* Warning when Signed is selected for crush */}
        {courageData.courageMode === 'signed' && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col gap-3 p-4 rounded-lg border border-destructive/30 bg-destructive/10"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
              <p className="text-sm text-muted-foreground">
                <strong>Your name will be visible</strong> to {recipientName || 'the recipient'}. If you'd like to stay anonymous, upgrade to unlock Quiet Courage.
              </p>
            </div>
            <Button
              variant="default"
              size="sm"
              className="w-full gap-2"
              onClick={async () => {
                if (saveAsDraft) {
                  toast.info('Saving your draft before upgrading…');
                  await saveAsDraft();
                }
                navigate('/add-credits');
              }}
            >
              <Sparkles className="h-4 w-4" />
              Upgrade to Stay Anonymous
            </Button>
          </motion.div>
        )}
      </div>

      {/* Reveal Policy (Legacy only, when Reveal When Ready selected) */}
      {courageData.courageMode === 'reveal_when_ready' && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="max-w-md mx-auto space-y-4">
          <Label className="text-sm font-medium text-muted-foreground">When should your identity be revealed?</Label>
          <div className="space-y-2">
            <motion.button
              onClick={() => updateCourageData({ revealPolicy: 'after_reply', revealAfterDays: null })}
              className={cn(
                'w-full flex items-center gap-3 p-4 rounded-lg border transition-all text-left',
                courageData.revealPolicy === 'after_reply'
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              )}
            >
              <div className={cn('h-4 w-4 rounded-full border-2', courageData.revealPolicy === 'after_reply' ? 'border-primary bg-primary' : 'border-muted-foreground')} />
              <span className="text-sm">Reveal after they reply</span>
            </motion.button>

            <motion.button
              onClick={() => updateCourageData({ revealPolicy: 'after_days', revealAfterDays: courageData.revealAfterDays || 3 })}
              className={cn(
                'w-full flex items-center gap-3 p-4 rounded-lg border transition-all text-left',
                courageData.revealPolicy === 'after_days'
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              )}
            >
              <div className={cn('h-4 w-4 rounded-full border-2', courageData.revealPolicy === 'after_days' ? 'border-primary bg-primary' : 'border-muted-foreground')} />
              <span className="text-sm">Reveal after a set number of days</span>
            </motion.button>
          </div>

          {courageData.revealPolicy === 'after_days' && (
            <Select
              value={String(courageData.revealAfterDays || 3)}
              onValueChange={(v) => updateCourageData({ revealAfterDays: parseInt(v) })}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select days" />
              </SelectTrigger>
              <SelectContent>
                {revealDayOptions.map((d) => (
                  <SelectItem key={d} value={String(d)}>{d} day{d > 1 ? 's' : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </motion.div>
      )}

      {/* Delivery Mode */}
      <div className="max-w-md mx-auto space-y-3">
        <Label className="text-sm font-medium text-muted-foreground mb-2 block">Delivery</Label>
        {deliveryOptions.map((option) => {
          const isSelected = courageData.deliveryMode === option.value;
          const Icon = option.icon;
          return (
            <motion.button
              key={option.value}
              onClick={() => updateCourageData({ deliveryMode: option.value })}
              className={cn(
                'w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left',
                isSelected
                  ? 'border-primary bg-primary/5 shadow-sm'
                  : 'border-border hover:border-primary/50 hover:bg-muted/50'
              )}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              {isSelected && (
                <motion.div className="absolute top-3 right-3" initial={{ scale: 0 }} animate={{ scale: 1 }}>
                  <Check className="h-4 w-4 text-primary" />
                </motion.div>
              )}
              <div className={cn(
                'flex items-center justify-center h-10 w-10 rounded-full shrink-0',
                isSelected ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
              )}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex flex-col">
                <span className={cn('text-sm font-medium', isSelected ? 'text-primary' : 'text-foreground')}>
                  {option.label}
                </span>
                <span className="text-xs text-muted-foreground">{option.helper}</span>
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Email inputs (when email relay selected) */}
      {courageData.deliveryMode === 'email_relay' && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="max-w-md mx-auto space-y-4">
          <div>
            <Label htmlFor="recipientEmail" className="text-sm text-muted-foreground mb-2 block">
              Recipient's email <span className="text-destructive">*</span>
            </Label>
            <Input
              id="recipientEmail"
              type="email"
              placeholder="their.email@example.com"
              value={courageData.recipientEmail}
              onChange={(e) => updateCourageData({ recipientEmail: e.target.value })}
              className="text-base py-5"
            />
          </div>
        </motion.div>
      )}

      {/* Safety notice */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="max-w-md mx-auto">
        <div className="bg-muted/50 border border-border rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
          <p className="text-xs text-muted-foreground">
            Anonymous messages are moderated and traceable internally for safety.
            All messages go through content review. Abuse will result in account suspension.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}
